import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calculator, Loader2, Plus, Pencil, Trash2, DollarSign, ShieldAlert, RefreshCw,
  FileSpreadsheet, FileText, AlertTriangle, CheckCircle2,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useAdmin } from "@/contexts/AdminContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PayrollApprovalBar } from "@/components/admin/timesheets/PayrollApprovalBar";
import { ReliabilityCard } from "@/components/admin/timesheets/ReliabilityCard";

// ── date helpers ──────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (val) => (val ? new Date(val).toISOString() : null);
const fmtCell = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
const currentWeek = () => {
  const now = new Date();
  const dow = (now.getDay() + 6) % 7; // 0 = Monday
  const mon = new Date(now); mon.setDate(now.getDate() - dow);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { start: toDateInput(mon), end: toDateInput(sun) };
};

// v2.8+E343 · one-tap payroll export (CSV / PDF) of the selected range.
const PayrollExportButtons = ({ rangeIso }) => {
  const [busy, setBusy] = useState(null);
  const download = async (kind) => {
    setBusy(kind);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
      const res = await adminApi.get(`/admin/timesheets/export.${kind}`, {
        params: { start: rangeIso.start, end: rangeIso.end, tz }, responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || `payroll.${kind}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Payroll ${kind.toUpperCase()} downloaded`);
    } catch (_e) {
      toast.error("Could not export payroll");
    } finally { setBusy(null); }
  };
  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={() => download("csv")} disabled={!!busy} data-testid="timesheets-export-csv" className="h-8 text-xs">
        {busy === "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1"/>}CSV
      </Button>
      <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={!!busy} data-testid="timesheets-export-pdf" className="h-8 text-xs">
        {busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <FileText className="h-3.5 w-3.5 mr-1"/>}PDF
      </Button>
    </div>
  );
};

export default function TimeSheetsPage() {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const isDispatcher = (admin?.role || "").toLowerCase() === "dispatcher";

  const week = useMemo(currentWeek, []);
  const [range, setRange] = useState({ start: week.start, end: week.end });
  const [filterId, setFilterId] = useState("all");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [rateDraft, setRateDraft] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ dispatcher_id: "", clock_in: "", clock_out: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [rateSearch, setRateSearch] = useState("");

  const filteredDispatchers = useMemo(() => {
    const q = rateSearch.trim().toLowerCase();
    if (!q) return dispatchers;
    return dispatchers.filter((d) =>
      (d.name || "").toLowerCase().includes(q) || (d.email || "").toLowerCase().includes(q));
  }, [dispatchers, rateSearch]);

  const rangeIso = useMemo(() => ({
    start: new Date(`${range.start}T00:00:00`).toISOString(),
    end: new Date(`${range.end}T23:59:59`).toISOString(),
  }), [range]);

  const loadDispatchers = useCallback(async () => {
    const { data } = await adminApi.get("/admin/timesheets/dispatchers");
    setDispatchers(data?.dispatchers || []);
    const draft = {};
    (data?.dispatchers || []).forEach((d) => { draft[d.id] = String(d.hourly_rate ?? 0); });
    setRateDraft(draft);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const entryParams = { start: rangeIso.start, end: rangeIso.end };
      if (filterId !== "all") entryParams.dispatcher_id = filterId;
      const [sm, en] = await Promise.all([
        adminApi.get("/admin/timesheets/summary", { params: { start: rangeIso.start, end: rangeIso.end } }),
        adminApi.get("/admin/timesheets/entries", { params: entryParams }),
      ]);
      setSummary(sm.data);
      setEntries(en.data?.entries || []);
    } catch (e) {
      if (!isDispatcher) toast.error(e?.response?.data?.detail || "Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, [rangeIso, filterId, isDispatcher]);

  useEffect(() => { if (!isDispatcher) loadDispatchers(); }, [isDispatcher, loadDispatchers]);
  useEffect(() => { if (!isDispatcher) load(); }, [isDispatcher, load]);

  const saveRate = async (id) => {
    const val = parseFloat(rateDraft[id]);
    if (Number.isNaN(val) || val < 0) { toast.error("Enter a valid rate"); return; }
    try {
      await adminApi.put(`/admin/timesheets/dispatchers/${id}/rate`, { hourly_rate: val });
      toast.success("Rate saved");
      await Promise.all([loadDispatchers(), load()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rate");
    }
  };

  const openEdit = (row) => {
    setEditRow({ ...row, _ci: isoToLocalInput(row.clock_in), _co: isoToLocalInput(row.clock_out), _note: row.note || "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/timesheets/entries/${editRow.id}`, {
        clock_in: localInputToIso(editRow._ci),
        clock_out: editRow._co ? localInputToIso(editRow._co) : "",
        note: editRow._note,
      });
      toast.success("Entry updated");
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update entry");
    } finally { setBusy(false); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this time entry? This changes pay totals and cannot be undone.")) return;
    try {
      await adminApi.delete(`/admin/timesheets/entries/${id}`);
      toast.success("Entry deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete entry");
    }
  };

  const markReviewed = async (id) => {
    try {
      await adminApi.post(`/admin/timesheets/entries/${id}/review`);
      toast.success("Marked as reviewed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update entry");
    }
  };

  const saveAdd = async () => {
    if (!addForm.dispatcher_id || !addForm.clock_in) { toast.error("Pick a staff member and a clock-in time"); return; }
    setBusy(true);
    try {
      await adminApi.post("/admin/timesheets/entries", {
        dispatcher_id: addForm.dispatcher_id,
        clock_in: localInputToIso(addForm.clock_in),
        clock_out: addForm.clock_out ? localInputToIso(addForm.clock_out) : null,
        note: addForm.note || null,
      });
      toast.success("Entry added");
      setAddOpen(false);
      setAddForm({ dispatcher_id: "", clock_in: "", clock_out: "", note: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add entry");
    } finally { setBusy(false); }
  };

  if (isDispatcher) {
    return (
      <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex items-center justify-center p-6">
        <Card className="p-8 text-center max-w-md">
          <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3"/>
          <h2 className="text-lg font-bold mb-1">Restricted</h2>
          <p className="text-sm text-zinc-500">Time Sheets are available to Field Supervisors and above.</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin")} data-testid="timesheets-back-denied" className="mt-4 h-8 text-xs">
            <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Console
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="timesheets-back-to-console" className="h-8 px-2 -ml-2 text-xs text-zinc-500 hover:text-amber-500">
          <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Console
        </Button>

        <div>
          <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2" data-testid="timesheets-title">
            <Calculator className="h-7 w-7 text-amber-500"/>Time Sheets
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Staff hours, hourly rates, and pay owed for any date range. Pay = hours × rate (flat).</p>
        </div>

        {/* Date range */}
        <Card className="p-4 flex flex-wrap items-end gap-3" data-testid="timesheets-controls">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} data-testid="timesheets-start-date" className="mt-1"/>
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} data-testid="timesheets-end-date" className="mt-1"/>
          </div>
          <Button variant="outline" onClick={load} data-testid="timesheets-refresh" className="h-10">
            <RefreshCw className="h-4 w-4 mr-1.5"/>Refresh
          </Button>
        </Card>

        <PayrollApprovalBar rangeStart={range.start} onWeekChange={setRange} refreshKey={summary}/>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-6 w-6 animate-spin"/></div>
        ) : (
          <>
            {summary?.totals?.flagged > 0 && (
              <div data-testid="timesheets-flagged-banner" className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4 shrink-0"/>
                <span><b>{summary.totals.flagged}</b> {summary.totals.flagged === 1 ? "entry was" : "entries were"} auto clocked-out by the Open-Shift Guard (30 min past scheduled end). Review below.</span>
              </div>
            )}

            {/* Pay summary */}
            <Card className="p-4" data-testid="timesheets-summary-card">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">Pay summary</div>
                <PayrollExportButtons rangeIso={rangeIso}/>
              </div>
              {(summary?.rows || []).length === 0 ? (
                <div className="text-sm text-zinc-400 py-6 text-center" data-testid="timesheets-summary-empty">No completed time entries in this range.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="timesheets-summary-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-200 dark:border-zinc-800">
                        <th className="py-2 pr-3">Staff</th>
                        <th className="py-2 px-3 text-right">Blocks</th>
                        <th className="py-2 px-3 text-right">Hours</th>
                        <th className="py-2 px-3 text-right">Rate</th>
                        <th className="py-2 pl-3 text-right">Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.rows.map((r) => (
                        <tr key={r.dispatcher_id} className="border-b border-zinc-100 dark:border-zinc-900" data-testid={`timesheets-summary-row-${r.dispatcher_id}`}>
                          <td className="py-2 pr-3 font-semibold">{r.dispatcher_name}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-zinc-500">{r.blocks}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                          <td className="py-2 px-3 text-right tabular-nums text-zinc-500">${r.hourly_rate.toFixed(2)}</td>
                          <td className="py-2 pl-3 text-right tabular-nums font-bold text-emerald-600 dark:text-emerald-300">${r.pay.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr className="font-black">
                        <td className="py-2 pr-3">Total</td>
                        <td/>
                        <td className="py-2 px-3 text-right tabular-nums" data-testid="timesheets-total-hours">{summary.totals.hours.toFixed(2)}</td>
                        <td/>
                        <td className="py-2 pl-3 text-right tabular-nums text-emerald-600 dark:text-emerald-300" data-testid="timesheets-total-pay">${summary.totals.pay.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <ReliabilityCard refreshKey={summary}/>

            {/* Hourly rates */}
            <Card className="p-4" data-testid="timesheets-rates-card">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 flex items-center gap-1.5"><DollarSign className="h-4 w-4"/>Hourly rates</div>
                {dispatchers.length > 6 && (
                  <Input
                    value={rateSearch} onChange={(e) => setRateSearch(e.target.value)}
                    placeholder="Search staff…" data-testid="timesheets-rate-search"
                    className="h-8 w-56 text-xs"
                  />
                )}
              </div>
              {filteredDispatchers.length === 0 ? (
                <div className="text-sm text-zinc-400 py-4 text-center">{dispatchers.length === 0 ? "No staff accounts found." : "No staff match your search."}</div>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredDispatchers.map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2" data-testid={`timesheets-rate-row-${d.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{d.name}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{d.email}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-zinc-400">$</span>
                        <Input
                          type="number" min="0" step="0.01" value={rateDraft[d.id] ?? ""}
                          onChange={(e) => setRateDraft((p) => ({ ...p, [d.id]: e.target.value }))}
                          data-testid={`timesheets-rate-input-${d.id}`} className="w-24 h-8 text-right"
                        />
                        <span className="text-[11px] text-zinc-500">/hr</span>
                        <Button size="sm" variant="outline" onClick={() => saveRate(d.id)} data-testid={`timesheets-rate-save-${d.id}`} className="h-8 text-xs">Save</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* Time entries */}
            <Card className="p-4" data-testid="timesheets-entries-card">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-widest font-bold text-zinc-500">Time entries ({entries.length})</div>
                <div className="flex items-center gap-2">
                  <Select value={filterId} onValueChange={setFilterId}>
                    <SelectTrigger data-testid="timesheets-filter-select" className="h-8 w-44 text-xs"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All staff</SelectItem>
                      {dispatchers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => setAddOpen(true)} data-testid="timesheets-add-entry-btn" className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                    <Plus className="h-3.5 w-3.5 mr-1"/>Add entry
                  </Button>
                </div>
              </div>
              {entries.length === 0 ? (
                <div className="text-sm text-zinc-400 py-6 text-center" data-testid="timesheets-entries-empty">No time entries in this range.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="timesheets-entries-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-200 dark:border-zinc-800">
                        <th className="py-2 pr-3">Staff</th>
                        <th className="py-2 px-3">Clock in</th>
                        <th className="py-2 px-3">Clock out</th>
                        <th className="py-2 px-3 text-right">Hours</th>
                        <th className="py-2 pl-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id} className="border-b border-zinc-100 dark:border-zinc-900" data-testid={`timesheets-entry-row-${e.id}`}>
                          <td className="py-2 pr-3 font-semibold">{e.dispatcher_name}</td>
                          <td className="py-2 px-3 tabular-nums">{fmtCell(e.clock_in)}</td>
                          <td className="py-2 px-3 tabular-nums">
                            {e.open ? <span className="text-emerald-600 dark:text-emerald-300 font-semibold">On shift</span> : fmtCell(e.clock_out)}
                            {e.auto_closed && (
                              <span
                                title={e.flag_reason || "Auto clocked-out at scheduled shift end"}
                                data-testid={`timesheets-flag-${e.id}`}
                                className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${e.flagged
                                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                                  : "bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"}`}
                              >
                                <AlertTriangle className="h-3 w-3"/>{e.flagged ? "Auto-closed" : "Auto-closed · reviewed"}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums">{e.open ? "—" : e.hours.toFixed(2)}</td>
                          <td className="py-2 pl-3 text-right whitespace-nowrap">
                            {e.flagged && (
                              <Button variant="ghost" size="icon" title="Mark reviewed" onClick={() => markReviewed(e.id)} data-testid={`timesheets-review-${e.id}`} className="h-7 w-7 text-amber-500 hover:text-emerald-500"><CheckCircle2 className="h-3.5 w-3.5"/></Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`timesheets-edit-${e.id}`} className="h-7 w-7 text-zinc-400 hover:text-amber-500"><Pencil className="h-3.5 w-3.5"/></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteEntry(e.id)} data-testid={`timesheets-delete-${e.id}`} className="h-7 w-7 text-zinc-400 hover:text-rose-500"><Trash2 className="h-3.5 w-3.5"/></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>

      {/* Edit entry dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="timesheets-edit-dialog">
          <DialogHeader><DialogTitle>Edit time entry</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-3">
              <div className="text-sm font-semibold">{editRow.dispatcher_name}</div>
              <div>
                <Label className="text-xs">Clock in</Label>
                <Input type="datetime-local" value={editRow._ci} onChange={(e) => setEditRow((r) => ({ ...r, _ci: e.target.value }))} data-testid="timesheets-edit-ci" className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs">Clock out <span className="text-zinc-400">(leave blank to reopen)</span></Label>
                <Input type="datetime-local" value={editRow._co} onChange={(e) => setEditRow((r) => ({ ...r, _co: e.target.value }))} data-testid="timesheets-edit-co" className="mt-1"/>
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea value={editRow._note} onChange={(e) => setEditRow((r) => ({ ...r, _note: e.target.value }))} data-testid="timesheets-edit-note" rows={2} className="mt-1"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="timesheets-edit-save" className="bg-amber-500 hover:bg-amber-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add manual entry dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="timesheets-add-dialog">
          <DialogHeader><DialogTitle>Add manual entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Staff member</Label>
              <Select value={addForm.dispatcher_id} onValueChange={(v) => setAddForm((f) => ({ ...f, dispatcher_id: v }))}>
                <SelectTrigger data-testid="timesheets-add-dispatcher" className="mt-1"><SelectValue placeholder="Select a staff member…"/></SelectTrigger>
                <SelectContent>{dispatchers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Clock in</Label>
              <Input type="datetime-local" value={addForm.clock_in} onChange={(e) => setAddForm((f) => ({ ...f, clock_in: e.target.value }))} data-testid="timesheets-add-ci" className="mt-1"/>
            </div>
            <div>
              <Label className="text-xs">Clock out <span className="text-zinc-400">(optional)</span></Label>
              <Input type="datetime-local" value={addForm.clock_out} onChange={(e) => setAddForm((f) => ({ ...f, clock_out: e.target.value }))} data-testid="timesheets-add-co" className="mt-1"/>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea value={addForm.note} onChange={(e) => setAddForm((f) => ({ ...f, note: e.target.value }))} data-testid="timesheets-add-note" rows={2} className="mt-1"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={saveAdd} disabled={busy} data-testid="timesheets-add-save" className="bg-amber-500 hover:bg-amber-600 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : null}Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
